import React, { useEffect, useState, useContext } from "react";
import { Table, Spinner, Alert, Container, Card, Button, Row, Col } from "react-bootstrap";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "../components/HusariaHeader";
import { useNavigate } from "react-router-dom";

const SystemStatus = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [systemInfo, setSystemInfo] = useState([]);

  const fetchSystemStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${serverHost}/sys_status`);
      if (!response.ok) throw new Error("Failed to fetch system status");
      const data = await response.json();

      setSystemInfo(data.result || []);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSystemStatus();
  }, []);

  // ---------------------------
  // CAPACITY PARSING (NEW)
  // ---------------------------
  const getNumericValue = (label) => {
    const item = systemInfo.find((i) => i.key === label);
    if (!item) return null;
    return parseFloat(item.value);
  };

  const diskTotal = getNumericValue("Disk Total");
  const diskFree = getNumericValue("Disk Free");
  const memoryTotal = getNumericValue("Memory Total");
  const memoryFree = getNumericValue("Memory Free");

  const diskUsagePct =
    diskTotal && diskFree
      ? Math.round(((diskTotal - diskFree) / diskTotal) * 100)
      : null;

  const memoryUsagePct =
    memoryTotal && memoryFree
      ? Math.round(((memoryTotal - memoryFree) / memoryTotal) * 100)
      : null;

  // ---------------------------
  // TABLE RENDER
  // ---------------------------
  const renderStatusTable = (title, data) => (
    <Card className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}>
      <Card.Header
        style={{
          backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
          color: isDark ? "#e2e8f0" : "#111",
        }}
      >
        <h5 className="mb-0">{title}</h5>
      </Card.Header>
      <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
        {data.length === 0 ? (
          <div
            className="text-center py-4"
            style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
          >
            <p className="mb-0">No data available</p>
          </div>
        ) : (
          <Table
            bordered
            className="mb-0"
            style={{
              backgroundColor: isDark ? "#1f2937" : "#fff",
              color: isDark ? "#e2e8f0" : "#212529",
            }}
          >
            <tbody>
              {data.map((item, index) => (
                <tr key={index}>
                  <td
                    style={{
                      fontWeight: 600,
                      width: "40%",
                      backgroundColor: isDark ? "#374151" : "#f8f9fa",
                      color: isDark ? "#e2e8f0" : "#212529",
                    }}
                  >
                    {item.key}
                  </td>
                  <td
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.9rem",
                      color: isDark ? "#d1d5db" : "#495057",
                    }}
                  >
                    {item.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card.Body>
    </Card>
  );

  return (
    <>
      <HusariaHeader name="System Status" parentName="Dealer" padding="4rem"/>

      <Container fluid className="mt--6">
        {/* Header */}
        <div style={{marginBottom: "8px"}}>
        <span className="return-dealer-menu" onClick={() => navigate('/admin/dealer-menu')}>
          <i class="fas fa-caret-left"></i> Return to Dealer Menu
        </span>
        </div>
        <Card className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light border-dark" : ""}`}>
          <Card.Body style={{ backgroundColor: isDark ? "#1f2937" : "#fff" }}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-1" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                  System Status Dashboard
                </h5>
                <small className="text-muted">
                  Last refreshed: {lastRefresh.toLocaleTimeString()}
                </small>
              </div>
              <div className="d-flex gap-2 align-items-center">
                {loading && (
                  <Spinner
                    animation="border"
                    size="sm"
                    variant={isDark ? "light" : "primary"}
                  />
                )}
                <Button
                  variant="info"
                  size="sm"
                  onClick={fetchSystemStatus}
                  disabled={loading}
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* System Info */}
        <Col className="mb-4">
          {renderStatusTable("System Information", systemInfo)}
        </Col>
      </Container>
    </>
  );
};

export default SystemStatus;
